// eslint-disable-next-line
import React, { useEffect, useMemo, useState } from "react";
import {
  Menu as MenuIcon,
  SettingsOutlined,
  ArrowDropDownOutlined,
  Search as SearchIcon,
  Check as CheckIcon,
  PersonAddAlt1Outlined,
} from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { setCredentials } from "state/authSlice";
import {
  AppBar,
  Button,
  IconButton,
  Toolbar,
  useTheme,
  Box,
  Typography,
  Divider,
  ListItemText,
  MenuItem,
  Menu,
  Tooltip,
  ListItemIcon,
} from "@mui/material";
import FlexBetween from "components/FlexBetween";
import { useLogout } from "state/hooks/useLogout";
import { useNavigate } from "react-router-dom";
import CommandPalette from "components/CommandPalette/CommandPalette";
import NotificationsBell from "components/NotificationsBell";
import { api, useGetLinkedAccountsQuery, useSwitchAccountMutation } from "state/api";
import {
  activateAccount,
  readSavedAccounts,
  startAddingAccount,
} from "state/savedAccounts";

// The round initial used for an account on phones and in the account list.
const Initial = ({ name, size = 32 }) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        width: size,
        height: size,
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        borderRadius: "50%",
        backgroundColor: theme.palette.extras.accent.subtle,
        color: theme.palette.extras.accent.main,
        fontWeight: 700,
        fontSize: size >= 32 ? "0.85rem" : "0.75rem",
      }}
    >
      {(name || "?").trim().charAt(0).toUpperCase()}
    </Box>
  );
};

function Navbar({ isSidebarOpen, setIsSidebarOpen, isNonMobile }) {
  const theme = useTheme();
  const { logout } = useLogout();
  const navigate = useNavigate();
  const user = useSelector((state) => state.auth.user);
  const dispatch = useDispatch();

  // Ctrl+K command palette — the app's one search surface (leads by
  // name/phone/email, clients, projects, jump-to-page). Replaces the old
  // navbar lead-name Autocomplete and its mobile search dialog.
  const [paletteOpen, setPaletteOpen] = useState(false);

  // Cmd/Ctrl-K toggles the palette from anywhere; the hint reflects the OS.
  const searchHint = useMemo(
    () =>
      typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform)
        ? "⌘K"
        : "Ctrl K",
    []
  );

  useEffect(() => {
    const onKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        setPaletteOpen((open) => !open);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  const [anchorEl, setAnchorEl] = useState(null);
  // Linked accounts — the same person's other roles. Empty for almost everyone,
  // so the menu is unchanged unless they actually have another account.
  const { data: linked } = useGetLinkedAccountsQuery(undefined, { skip: !user });
  const [switchAccount, { isLoading: switching }] = useSwitchAccountMutation();
  const linkedAccounts = linked?.accounts || [];

  const handleSwitchAccount = async (id) => {
    try {
      const r = await switchAccount(id).unwrap();
      // Swap the session in place, then land on the new account's own home —
      // a Media Buyer's URL is meaningless to an Account Manager and would
      // greet them with a 404 the moment they switched.
      dispatch(setCredentials({ user: r.user, token: r.token }));
      setAnchorEl(null);
      window.location.assign("/");
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert(e?.data?.error || "Couldn't switch accounts.");
    }
  };
  const isOpen = Boolean(anchorEl);

  // Accounts signed in on this device (state/savedAccounts.js) — one tap
  // opens another, like Instagram. Read when the menu opens: the list lives
  // in localStorage and changes outside React.
  const savedAccounts = useMemo(
    () => (isOpen ? readSavedAccounts() : []),
    [isOpen]
  );
  const handleOpenSaved = (account) => {
    setAnchorEl(null);
    if (String(account.user._id) === String(user?._id)) return;
    activateAccount(dispatch, account, { resetApi: api.util.resetApiState });
    navigate("/");
  };
  const handleAddAccount = () => {
    setAnchorEl(null);
    startAddingAccount(dispatch, { resetApi: api.util.resetApiState });
    navigate("/login");
  };

  const handleLogout = () => {
    setAnchorEl(null);
    logout();
  };

  return (
    <AppBar
      sx={{
        position: "static",
        background: "none",
        boxShadow: "none",
        borderBottom: `1px solid ${theme.palette.extras.surface.borderSubtle}`,
        // Phones: the bar rides a sticky wrapper (Layout.js) — give it the
        // frosted-glass treatment matching BottomNav, with a solid fallback,
        // and absorb the iOS status-bar inset (black-translucent chrome).
        "@media (max-width:600px)": {
          backgroundColor:
            theme.palette.extras?.surface?.base || "#1a2a27",
          borderBottomColor:
            theme.palette.extras?.glass?.edge || "rgba(255,255,255,0.10)",
          paddingTop: "var(--safe-area-top, 0px)",
          "@supports ((backdrop-filter: blur(1px)) or (-webkit-backdrop-filter: blur(1px)))":
            {
              backgroundColor:
                theme.palette.extras?.glass?.bg || "rgba(26, 42, 39, 0.78)",
              backdropFilter:
                theme.palette.extras?.glass?.blur || "saturate(1.5) blur(16px)",
              WebkitBackdropFilter:
                theme.palette.extras?.glass?.blur || "saturate(1.5) blur(16px)",
            },
        },
      }}
    >
      <Toolbar sx={{ justifyContent: "space-between", py: 1 }}>
        <FlexBetween
          backgroundColor={
            isNonMobile ? theme.palette.background.alt : "transparent"
          }
          borderRadius={`${theme.palette.extras.radius.md}px`}
          gap="1rem"
          p={isNonMobile ? "0.25rem 1rem" : 0}
          sx={{
            border: isNonMobile
              ? `1px solid ${theme.palette.extras.surface.borderSubtle}`
              : "none",
          }}
        >
          <Tooltip title="Toggle menu">
            <IconButton onClick={() => setIsSidebarOpen(!isSidebarOpen)}>
              <MenuIcon
                sx={{
                  color: isNonMobile
                    ? theme.palette.secondary[200]
                    : theme.palette.extras.surface.textHigh,
                }}
              />
            </IconButton>
          </Tooltip>
          {isNonMobile && (
            // Styled like the old search box, but it's a trigger — the real
            // input lives inside the palette dialog.
            <Box
              component="button"
              type="button"
              aria-label="Search (Ctrl+K)"
              onClick={() => setPaletteOpen(true)}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "0.6rem",
                width: 300,
                py: "0.55rem",
                px: "0.25rem",
                background: "transparent",
                border: "none",
                cursor: "pointer",
                color: theme.palette.secondary[200],
                fontFamily: "inherit",
              }}
            >
              <SearchIcon sx={{ fontSize: 20, opacity: 0.85 }} />
              <Typography
                sx={{
                  flex: 1,
                  textAlign: "left",
                  fontSize: "0.875rem",
                  color: "inherit",
                  opacity: 0.75,
                }}
              >
                Search…
              </Typography>
              <Box
                component="span"
                sx={{
                  px: 0.75,
                  py: 0.25,
                  fontSize: "0.7rem",
                  fontWeight: 600,
                  lineHeight: 1.5,
                  color: "rgba(13, 47, 44, 0.6)",
                  border: "1px solid rgba(13, 47, 44, 0.2)",
                  borderRadius: "6px",
                  whiteSpace: "nowrap",
                  pointerEvents: "none",
                }}
              >
                {searchHint}
              </Box>
            </Box>
          )}
          {!isNonMobile && (
            <Tooltip title="Search">
              <IconButton
                aria-label="Search"
                onClick={() => setPaletteOpen(true)}
              >
                <SearchIcon
                  sx={{ color: theme.palette.extras.surface.textHigh }}
                />
              </IconButton>
            </Tooltip>
          )}
        </FlexBetween>

        <FlexBetween sx={{ gap: { xs: "0.25rem", sm: "1rem" } }}>
          {/* Bell + gear read as one icon cluster: a tight gap between them,
              the wider gap only before the account menu. */}
          <FlexBetween sx={{ gap: "0.125rem" }}>
            <NotificationsBell />
            <Tooltip title="Settings">
              <IconButton onClick={() => navigate("/settings")}>
                <SettingsOutlined />
              </IconButton>
            </Tooltip>
          </FlexBetween>

          <FlexBetween>
            {isNonMobile ? (
              <Button
                onClick={(event) => setAnchorEl(event.currentTarget)}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  textTransform: "none",
                  gap: "1rem",
                }}
              >
                <Box textAlign="left">
                  <Typography
                    fontWeight="bold"
                    fontSize="0.85rem"
                    sx={{ color: theme.palette.primary[500] }}
                  >
                    {user.name}
                  </Typography>
                  <Typography
                    fontSize="0.75rem"
                    sx={{ color: theme.palette.primary[500] }}
                  >
                    {user.occupation}
                  </Typography>
                </Box>
                <ArrowDropDownOutlined
                  sx={{ color: theme.palette.primary[500], fontSize: "25px" }}
                />
              </Button>
            ) : (
              // Phones: the name/occupation text block is too wide at 375px —
              // a compact avatar circle opens the same account menu (this menu
              // is the only mobile logout, so it must stay reachable).
              <IconButton
                aria-label="Account menu"
                onClick={(event) => setAnchorEl(event.currentTarget)}
                sx={{ p: 0.5 }}
              >
                <Initial name={user.name} />
              </IconButton>
            )}
            <Menu
              anchorEl={anchorEl}
              open={isOpen}
              onClose={() => setAnchorEl(null)}
              anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
              <MenuItem key="saved-label" disabled sx={{ opacity: 0.7, fontSize: 12 }}>
                Accounts on this device
              </MenuItem>
              {savedAccounts.map((a) => {
                const current = String(a.user._id) === String(user?._id);
                return (
                  <MenuItem
                    key={`saved-${a.user._id}`}
                    selected={current}
                    onClick={() => handleOpenSaved(a)}
                    sx={{ gap: 1.5, minWidth: 240 }}
                  >
                    <Initial name={a.user.name} size={28} />
                    <ListItemText
                      primary={a.user.name}
                      secondary={a.user.email || a.user.role}
                    />
                    {current && <CheckIcon fontSize="small" />}
                  </MenuItem>
                );
              })}
              <MenuItem key="saved-add" onClick={handleAddAccount}>
                <ListItemIcon>
                  <PersonAddAlt1Outlined fontSize="small" />
                </ListItemIcon>
                Add another account
              </MenuItem>
              <Divider key="saved-divider" />
              {linkedAccounts.length > 0 && [
                <MenuItem key="switch-label" disabled sx={{ opacity: 0.7, fontSize: 12 }}>
                  Switch account
                </MenuItem>,
                ...linkedAccounts.map((a) => (
                  <MenuItem
                    key={a._id}
                    disabled={switching}
                    onClick={() => handleSwitchAccount(a._id)}
                  >
                    <ListItemText primary={a.name} secondary={a.role} />
                  </MenuItem>
                )),
                <Divider key="switch-divider" />,
              ]}
              <MenuItem onClick={handleLogout}>
                {savedAccounts.length > 1
                  ? `Log out of ${user.name}`
                  : "Log Out"}
              </MenuItem>
            </Menu>
          </FlexBetween>
        </FlexBetween>
      </Toolbar>

      <CommandPalette
        open={paletteOpen}
        onClose={() => setPaletteOpen(false)}
        isNonMobile={isNonMobile}
      />
    </AppBar>
  );
}

export default Navbar;
